import React, { useContext, useRef, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { navBarStyles } from './style';
import { Drawer, List } from '@mui/material';
import { GlobalLogo } from 'components';
import { DrawerContext, FindLoad, Images, OrgIntegrations, superAdminRouters, useSecondaryModal } from 'utils';
import { CustomListItem, HelpCenterAccordion } from './common';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { SupportModalSelector } from './common/supportModalSelector';
import { Skeleton } from '@mui/material';
import { versionsActions, wishlistActions } from 'store';

const ACTION_TYPE = 'GET_COMPANY';
export const LeftBar = ({}) => {
    const classes = navBarStyles();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { open, handleDrawerOpenClose, setOpen, handleBlockHoverOpen, block } = useContext(DrawerContext);
    const company = useSelector((state) => state.system.company);
    const { openSecondary } = useSecondaryModal();
    const loader = FindLoad(ACTION_TYPE);
    const hoverTimeout = useRef(null);
    const { cSession } = OrgIntegrations();
    const publishedVersions = useSelector((state) => state.versions.publishedVersions);

    const hasNewVersions = useMemo(() => {
        if (!publishedVersions?.length) return false;
        const lastSeenId = localStorage.getItem('last_seen_version_id');
        if (!lastSeenId) return true;
        return publishedVersions[0]._id !== lastSeenId;
    }, [publishedVersions]);

    const isOnReleaseNotes = location?.pathname === '/release-notes';
    const isOnWishlist = location?.pathname?.startsWith('/wishlist');
    const wishlistUpdates = useSelector((state) => state.wishlist?.updatesCount);

    // Re-read on every navigation: a cheap indexed count, and the only way the
    // dot learns about a TherapyLake update until wishlist notifications exist.
    useEffect(() => {
        dispatch(wishlistActions.getWishlistUpdatesCount());
    }, [location?.pathname]);

    useEffect(() => {
        dispatch(versionsActions.getPublishedVersions());
    }, []);

    const handleMouseEnter = () => {
        hoverTimeout.current = setTimeout(() => {
            handleDrawerOpenClose(true);
        }, 500);
    };

    const handleMouseLeave = () => {
        clearTimeout(hoverTimeout.current);
        handleDrawerOpenClose(false);
    };

    return (
        <div style={{ position: block ? 'relative' : 'fixed', zIndex: 1205 }}>
            <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <Drawer
                    variant="permanent"
                    className={clsx(classes.drawer, {
                        [classes.drawerOpen]: open,
                        [classes.drawerClose]: !open,
                    })}
                    classes={{
                        paper: clsx({
                            [classes.drawerOpen]: open,
                            [classes.drawerClose]: !open,
                        }),
                    }}
                >
                    <div className={classes.drawerBox}>
                        <div>
                            <div className={classes.companyInfoBox}>
                                {loader?.length ? (
                                    <Skeleton variant="rounded" width={32} height={32} />
                                ) : (
                                    company?.name && (
                                        <>
                                            <div className={classes.companyInfoWrapper}>
                                                <div className={company?.logo?.url ? classes.companyLogo : classes.companyBox}>
                                                    <img
                                                        src={company?.logo?.url ? company?.logo?.url : Images.office}
                                                        alt="icon"
                                                    />
                                                </div>
                                                {open && <p>{company?.name ? company?.name : ''}</p>}
                                            </div>
                                        </>
                                    )
                                )}
                            </div>
                            <List className={classes.menuItems} style={{ width: open ? 220 : 64 }}>
                                {superAdminRouters(cSession).map((item, index) => {
                                    if (item) {
                                        return <CustomListItem key={index} item={item} accordion={item?.type === 'accordion'} />;
                                    }
                                })}
                            </List>
                        </div>

                        <div style={{ position: 'relative', zIndex: 1205, width: open ? 220 : 64 }}>
                            <div className={classes.helpSupportWrapper}>
                                <HelpCenterAccordion />
                                <div onClick={() => openSecondary(<SupportModalSelector />)} className={classes.helpCenterBox}>
                                    <button style={{ width: open ? '100%' : 'auto' }} className={classes.helpCenterButton}>
                                        <div>
                                            <img src={Images.onlineSupport} alt="" />
                                        </div>
                                        {open && <p>Support</p>}
                                    </button>
                                </div>
                                {/* Ungated: every staff user can file and read their org's wishlist. */}
                                <div onClick={() => navigate('/wishlist')} className={classes.helpCenterBox}>
                                    <button
                                        style={{ width: open ? '100%' : 'auto', position: 'relative' }}
                                        className={classes.helpCenterButton}
                                    >
                                        <div>
                                            <div>
                                                <img src={isOnWishlist ? Images.toolsTabActive : Images.toolsTab} alt="wishlist" />
                                            </div>
                                            {/* Same dot as Release Notes: one of my requests has a TherapyLake update. */}
                                            {wishlistUpdates > 0 && !isOnWishlist && <span className={classes.isOnReleaseNotes} />}
                                        </div>
                                        {open && <p style={isOnWishlist ? { color: '#347AF0' } : {}}>Wishlist</p>}
                                    </button>
                                </div>
                                <div
                                    onClick={() => navigate('/release-notes')}
                                    className={classes.helpCenterBox}
                                >
                                    <button
                                        style={{ width: open ? '100%' : 'auto', position: 'relative' }}
                                        className={classes.helpCenterButton}
                                    >
                                        <div>
                                            <div>
                                                <img src={isOnReleaseNotes ? Images.releaseNotesActive : Images.releaseNotes} alt="releaseNotes" />
                                            </div>

                                            {hasNewVersions && !isOnReleaseNotes && (
                                                <span className={classes.isOnReleaseNotes} />
                                            )}
                                        </div>
                                        {open && (
                                            <p style={isOnReleaseNotes ? { color: '#347AF0' } : {}}>
                                                Release Notes
                                            </p>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/*<div className={classes.myProfileWrapper}>*/}
                            {/*    <button onClick={() =>*/}
                            {/*        RolePermission([PermissionList.STAFF_READ.code]) && window.open(`/staff/${userInfo?.id}`)*/}
                            {/*    }*/}
                            {/*    >*/}
                            {/*        <div className={classes.userImage}>*/}
                            {/*            <img src={userInfo?.avatar?.url ? userInfo?.avatar?.url : images.userProfile} alt="Avatar" />*/}
                            {/*        </div>*/}
                            {/*        {open &&*/}
                            {/*            <div className="column" style={{ gap: 4 }}>*/}
                            {/*                <TextRow name={renderStaffName(userInfo)} fontWeight={600} color={'#414651'} wrapperStyle={{fontSize:'14px'}}  />*/}
                            {/*                <TextRow name={userInfo?.email} color={'#535862'} wrapperStyle={{fontSize:'14px'}}  />*/}
                            {/*            </div>*/}
                            {/*        }*/}
                            {/*    </button>*/}
                            {/*</div>*/}

                            <button
                                style={{ marginTop: 10 }}
                                onMouseLeave={() => handleDrawerOpenClose(open)}
                                onMouseEnter={() => handleDrawerOpenClose(open)}
                                onClick={() => handleBlockHoverOpen()}
                                className={classes.collapseBtn}
                            >
                                {open && <p>Collapse</p>}
                                <img style={{ transition: 'all 0.3s' }} src={open ? Images.cloeSafearea : Images.openSafearea} alt="icon" />
                            </button>

                            <div style={{ height: 30 }}>
                                <GlobalLogo open={open} />
                            </div>
                        </div>
                    </div>
                </Drawer>
            </div>
        </div>
    );
};
